import { useEffect, useState } from "react";

import { ShieldAlert, User, Copy, X } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

type Item = {
  id: string;
  user_id: string;
  content: string;
  created_at: string;
  name: string;
};

const MARKER = "Refusal Strategy Matrix";

export function AdminRefusalStrategies() {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState<Item | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data: msgs } = await supabase
        .from("student_messages")
        .select("id, user_id, content, created_at")
        .ilike("content", `%${MARKER}%`)
        .eq("is_from_admin", false)
        .order("created_at", { ascending: false })
        .limit(20);
      if (!mounted) return;
      const rows = msgs ?? [];
      const ids = Array.from(new Set(rows.map((r) => r.user_id)));
      const nameMap = new Map<string, string>();
      if (ids.length) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("user_id, name")
          .in("user_id", ids);
        for (const p of profs ?? []) if (p.user_id && p.name) nameMap.set(p.user_id, p.name);
        const { data: pays } = await supabase
          .from("payments")
          .select("user_id, buyer_name")
          .in("user_id", ids);
        for (const p of pays ?? []) if (p.user_id && !nameMap.has(p.user_id) && p.buyer_name) nameMap.set(p.user_id, p.buyer_name);
      }
      // dedupe to latest per user
      const seen = new Set<string>();
      const deduped: Item[] = [];
      for (const r of rows) {
        if (seen.has(r.user_id)) continue;
        seen.add(r.user_id);
        deduped.push({ ...r, name: nameMap.get(r.user_id) || "Student" });
      }
      setItems(deduped.slice(0, 6));
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActive(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  if (loading) {
    return (
      <p className="mt-8 text-center text-xs text-muted-foreground">Loading refusal strategies…</p>
    );
  }

  if (items.length === 0) {
    return (
      <p className="mt-8 text-center text-xs text-muted-foreground/80">
        No pending Refusal Strategies to review.
      </p>
    );
  }

  return (
    <div className="mt-8 rounded-3xl border border-border bg-card p-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="grid place-items-center h-9 w-9 rounded-2xl bg-destructive/10 text-destructive">
            <ShieldAlert className="h-4 w-4" />
          </div>
          <div>
            <h2 className="font-display text-xl font-semibold">Refusal Strategies · 214(b)</h2>
            <p className="text-xs text-muted-foreground">
              Latest matrices submitted by students. Click a card to read the full submission.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {items.map((it) => (
          <button
            key={it.id}
            type="button"
            onClick={() => setActive(it)}
            className="block w-full text-left rounded-xl border border-border/60 p-3 transition hover:border-primary/60 hover:bg-muted/40"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <span className="inline-flex items-center gap-2 font-medium">
                <User className="h-4 w-4 text-muted-foreground" />
                {it.name}
              </span>
              <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(it.created_at), { addSuffix: true })}
              </span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground line-clamp-2 whitespace-pre-line">
              {it.content.replace(/[*_📋🎓💰🗣]/g, "").trim().slice(0, 160)}…
            </p>
          </button>
        ))}
      </div>

      {active && <FullSubmissionModal item={active} onClose={() => setActive(null)} />}
    </div>
  );
}

function FullSubmissionModal({ item, onClose }: { item: Item; onClose: () => void }) {
  const plain = item.content.replace(/\*/g, "").trim();

  const copyAll = async () => {
    const text = `${item.name} · ${format(new Date(item.created_at), "d MMM yyyy, h:mm a")}\n\n${plain}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Full submission copied to clipboard.");
    } catch {
      toast.error("Couldn't copy — select the text manually.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Refusal strategy submission from ${item.name}`}
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-2xl overflow-hidden rounded-3xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <h3 className="font-display text-lg font-semibold">{item.name}</h3>
            <p className="text-xs text-muted-foreground">
              Submitted {format(new Date(item.created_at), "d MMM yyyy, h:mm a")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={copyAll}
              className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold transition hover:bg-muted"
            >
              <Copy className="h-3.5 w-3.5" /> Copy All Text
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="grid h-8 w-8 place-items-center rounded-full border border-border transition hover:bg-muted"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="max-h-[65vh] overflow-y-auto px-5 py-4">
          <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-foreground">
            {plain}
          </pre>
        </div>
      </div>
    </div>
  );
}
